/**
 * Grid Delta Hedge — maintains a partial short to offset underwater long fills.
 *
 * When the grid accumulates open buy fills (price dropped through buy levels),
 * the hedge opens a short position sized at HEDGE_RATIO of the total long exposure.
 * This limits drawdown during directional drops while preserving most grid profit
 * during ranging markets.
 *
 * Mechanics:
 *   - Each tick: compute total long exposure from open fills
 *   - Desired short = exposure × HEDGE_RATIO
 *   - If current hedge size differs by >10%, adjust
 *   - Track hedge PnL separately from grid PnL
 *   - Close hedge when open fills drop below threshold (price recovered)
 *
 * Cost analysis (30% hedge on $6,400 exposure):
 *   - Ranging drag: ~$8/day (5.6% of grid profit)
 *   - Funding income: ~$2/day (shorts earn when rate positive)
 *   - Net cost: ~$6/day — pays for itself on any >1.3% drop
 */

import { readFile, writeFile, mkdir } from 'node:fs/promises';
import { dirname } from 'node:path';
import { gridStatePath } from './paths.js';
import chalk from 'chalk';

/** Default fraction of grid long exposure to hedge with a short.
 *  Used as the chop/neutral baseline when no `marketTrend` is supplied or
 *  trend is in the [-0.10, +0.10] range. See `hedgeRatioFor()` for the
 *  trend-adaptive table. Override default via HEDGE_RATIO env var. */
const HEDGE_RATIO_DEFAULT = Number.isFinite(Number(process.env.HEDGE_RATIO))
  ? Number(process.env.HEDGE_RATIO)
  : 0.30;

/**
 * Trend-adaptive hedge ratio.
 *
 * `marketTrend` is the recent BTC % move (negative = bearish), computed by
 * the manager over the same 56h / 14-bar window the asymmetric-grid filter
 * uses. Because that window is short, the magnitudes are small even in
 * sustained bears — empirically: BTC dropping ~1% / day prints a 56h
 * trend of −2-3%; the worst 56h slices in BEAR-30d-jan26 hit roughly −7%.
 * The thresholds below are calibrated to that scale, NOT to multi-week
 * cumulative drawdown.
 *
 * Bear-window backtest sweep (Oct '25 – Feb '26 BTC drawdowns):
 *   - 50% hedge in bear cuts loss by ~25 ret pts vs 30%
 *   - 10% hedge in bull saves ~7 ret pts vs 30% drag in chop
 *
 * Setting HEDGE_RATIO env var bypasses the table — useful for sweeps.
 */
export function hedgeRatioFor(marketTrend: number | undefined): number {
  if (process.env.HEDGE_RATIO !== undefined && Number.isFinite(Number(process.env.HEDGE_RATIO))) {
    return Number(process.env.HEDGE_RATIO);
  }
  if (marketTrend === undefined || !Number.isFinite(marketTrend)) {
    return HEDGE_RATIO_DEFAULT;
  }
  if (marketTrend <= -0.05) return 0.70; // strong bear (~ -1%/day sustained)
  if (marketTrend <= -0.025) return 0.50; // confirmed bear
  if (marketTrend >=  0.025) return 0.10; // strong bull — minimize drag
  return HEDGE_RATIO_DEFAULT;            // chop / neutral
}

/** Minimum number of open buy fills before hedge activates. */
const MIN_FILLS_TO_HEDGE = 3;

/** Don't adjust hedge unless size differs by more than this fraction. */
const ADJUSTMENT_THRESHOLD = 0.10;

export interface HedgePosition {
  token: string;
  /** Short entry price (average, updates on adjustments). */
  entryPrice: number;
  /** Short quantity in token units. */
  quantity: number;
  /** Timestamp of last adjustment. */
  lastAdjustedAt: number;
  /** Cumulative realized PnL from hedge adjustments. */
  realizedPnl: number;
}

export interface HedgeState {
  positions: HedgePosition[];
  /** Total realized PnL across all hedge positions (including closed). */
  totalRealizedPnl: number;
  /** Today's realized PnL (resets daily). */
  todayRealizedPnl: number;
  lastDailyReset: number;
}

export interface OpenFillExposure {
  token: string;
  totalQuantity: number;
  totalNotional: number;
  avgEntryPrice: number;
  fillCount: number;
}

export interface HedgeTickResult {
  /** Number of hedge adjustments made this tick. */
  adjustments: number;
  /** Unrealized PnL across all active hedge positions. */
  unrealizedPnl: number;
  /** Total realized PnL (cumulative). */
  totalRealizedPnl: number;
}

export class GridHedgeManager {
  private state: HedgeState | null = null;
  private statePath: string;

  constructor(stateDir?: string) {
    this.statePath = gridStatePath('hedge.json', stateDir);
  }

  async load(now: number = Date.now()): Promise<HedgeState> {
    if (this.state) return this.state;
    try {
      const raw = await readFile(this.statePath, 'utf-8');
      this.state = JSON.parse(raw) as HedgeState;
    } catch {
      this.state = {
        positions: [],
        totalRealizedPnl: 0,
        todayRealizedPnl: 0,
        lastDailyReset: now,
      };
    }

    // Reset daily PnL at UTC midnight (uses `now` so backtester can drive it)
    const todayMidnight = new Date(now);
    todayMidnight.setUTCHours(0, 0, 0, 0);
    if (this.state.lastDailyReset < todayMidnight.getTime()) {
      this.state.todayRealizedPnl = 0;
      this.state.lastDailyReset = now;
    }

    return this.state;
  }

  async save(): Promise<void> {
    if (!this.state) return;
    await mkdir(dirname(this.statePath), { recursive: true });
    const tmp = `${this.statePath}.tmp.${process.pid}`;
    await writeFile(tmp, JSON.stringify(this.state, null, 2), 'utf-8');
    const { rename } = await import('node:fs/promises');
    await rename(tmp, this.statePath);
  }

  /**
   * Run one hedge tick. Call after grid tick with current prices and open fill data.
   *
   * @param openFills - Per-token open fill exposure from the grid portfolio
   * @param prices - Current mark prices per token
   * @param now - injected clock (backtester drives this)
   * @param marketTrend - recent market trend (e.g. BTC's 56h trend) used by
   *                     `hedgeRatioFor()` to scale hedge size by regime. Pass
   *                     undefined to use the static default.
   * @param paused - when true, force desired hedge to zero for all tokens so
   *                 the close-path realizes any unrealized PnL into the
   *                 ledger. The loop passes `state.paused` so a soft-pause
   *                 grid doesn't hold paper-gain on the hedge that erodes
   *                 when the underlying mean-reverts toward the resume
   *                 threshold.
   */
  async tick(
    openFills: OpenFillExposure[],
    prices: Record<string, number>,
    now: number = Date.now(),
    marketTrend?: number,
    paused: boolean = false,
  ): Promise<HedgeTickResult> {
    const state = await this.load(now);
    let adjustments = 0;
    let unrealizedPnl = 0;

    // Resolve the active hedge ratio once per tick so all per-token sizing
    // and event logging use the same value.
    const activeRatio = hedgeRatioFor(marketTrend);

    for (const fill of openFills) {
      const price = prices[fill.token];
      if (!price || price <= 0) continue;

      // When the grid is soft-paused, force zero target. The existing close
      // branch below crystallizes unrealized PnL into the ledger — preserving
      // the protection value the hedge accrued during the drawdown instead
      // of giving it back when the underlying recovers toward unpause.
      const desiredShortQty = paused
        ? 0
        : fill.fillCount >= MIN_FILLS_TO_HEDGE
          ? fill.totalQuantity * activeRatio
          : 0;

      const existing = state.positions.find(p => p.token === fill.token);

      if (desiredShortQty <= 0) {
        // Close hedge if one exists
        if (existing && existing.quantity > 0) {
          const closePnl = (existing.entryPrice - price) * existing.quantity;
          state.totalRealizedPnl += closePnl;
          state.todayRealizedPnl += closePnl;
          existing.realizedPnl += closePnl;
          console.error(chalk.yellow(
            `  [hedge] CLOSE ${fill.token} short ${existing.quantity.toFixed(6)} @ $${price.toFixed(2)} ` +
            `PnL: $${closePnl.toFixed(2)}`
          ));
          existing.quantity = 0;
          existing.entryPrice = 0;
          adjustments++;
        }
        continue;
      }

      if (!existing) {
        // Open new hedge
        state.positions.push({
          token: fill.token,
          entryPrice: price,
          quantity: desiredShortQty,
          lastAdjustedAt: Date.now(),
          realizedPnl: 0,
        });
        console.error(chalk.magenta(
          `  [hedge] OPEN ${fill.token} short ${desiredShortQty.toFixed(6)} @ $${price.toFixed(2)} ` +
          `(${(activeRatio * 100).toFixed(0)}% of ${fill.fillCount} open fills)`
        ));
        adjustments++;
      } else if (existing.quantity > 0) {
        // Check if adjustment needed
        const diff = Math.abs(desiredShortQty - existing.quantity) / existing.quantity;
        if (diff > ADJUSTMENT_THRESHOLD) {
          if (desiredShortQty > existing.quantity) {
            // Increase hedge — add to short
            const addQty = desiredShortQty - existing.quantity;
            const newAvgEntry = (existing.entryPrice * existing.quantity + price * addQty) / desiredShortQty;
            existing.entryPrice = newAvgEntry;
            existing.quantity = desiredShortQty;
            existing.lastAdjustedAt = Date.now();
            console.error(chalk.magenta(
              `  [hedge] INCREASE ${fill.token} short to ${desiredShortQty.toFixed(6)} @ avg $${newAvgEntry.toFixed(2)}`
            ));
            adjustments++;
          } else {
            // Decrease hedge — partially close short
            const closeQty = existing.quantity - desiredShortQty;
            const closePnl = (existing.entryPrice - price) * closeQty;
            state.totalRealizedPnl += closePnl;
            state.todayRealizedPnl += closePnl;
            existing.realizedPnl += closePnl;
            existing.quantity = desiredShortQty;
            existing.lastAdjustedAt = Date.now();
            console.error(chalk.magenta(
              `  [hedge] DECREASE ${fill.token} short to ${desiredShortQty.toFixed(6)}, ` +
              `closed ${closeQty.toFixed(6)} PnL: $${closePnl.toFixed(2)}`
            ));
            adjustments++;
          }
        }

        // Compute unrealized PnL on current hedge
        const uPnl = (existing.entryPrice - price) * existing.quantity;
        unrealizedPnl += uPnl;
      } else {
        // Hedge was closed but fills are back — reopen
        existing.entryPrice = price;
        existing.quantity = desiredShortQty;
        existing.lastAdjustedAt = Date.now();
        console.error(chalk.magenta(
          `  [hedge] REOPEN ${fill.token} short ${desiredShortQty.toFixed(6)} @ $${price.toFixed(2)}`
        ));
        adjustments++;
      }
    }

    // Close hedges for tokens no longer in the fill list
    for (const pos of state.positions) {
      if (pos.quantity <= 0) continue;
      if (!openFills.find(f => f.token === pos.token)) {
        const price = prices[pos.token];
        if (price) {
          const closePnl = (pos.entryPrice - price) * pos.quantity;
          state.totalRealizedPnl += closePnl;
          state.todayRealizedPnl += closePnl;
          pos.realizedPnl += closePnl;
          console.error(chalk.yellow(
            `  [hedge] CLOSE orphaned ${pos.token} short, PnL: $${closePnl.toFixed(2)}`
          ));
          pos.quantity = 0;
          adjustments++;
        }
      }
    }

    await this.save();

    return {
      adjustments,
      unrealizedPnl,
      totalRealizedPnl: state.totalRealizedPnl,
    };
  }

  /**
   * Close every active hedge position in the ledger at the provided mark
   * prices, accumulating realized PnL using the same `(entry - price) * qty`
   * formula `tick()` uses on its auto-close path. Positions whose token is
   * missing from `prices` are left untouched and reported in `skipped`.
   *
   * Bookkeeping-only — this manager does not place perp orders; the actual
   * HL position (if any) must be closed externally. Use this to zero the
   * ledger as part of `sherwood grid flatten`.
   */
  async flatten(prices: Record<string, number>, now: number = Date.now()): Promise<{
    closed: Array<{ token: string; quantity: number; realizedPnl: number }>;
    skipped: string[];
    totalRealizedPnl: number;
  }> {
    const state = await this.load(now);
    const closed: Array<{ token: string; quantity: number; realizedPnl: number }> = [];
    const skipped: string[] = [];

    for (const pos of state.positions) {
      if (pos.quantity <= 0) continue;
      const price = prices[pos.token];
      if (!price || price <= 0) {
        skipped.push(pos.token);
        continue;
      }
      const closePnl = (pos.entryPrice - price) * pos.quantity;
      state.totalRealizedPnl += closePnl;
      state.todayRealizedPnl += closePnl;
      pos.realizedPnl += closePnl;
      closed.push({ token: pos.token, quantity: pos.quantity, realizedPnl: closePnl });
      pos.quantity = 0;
      pos.entryPrice = 0;
      pos.lastAdjustedAt = now;
    }

    await this.save();
    return { closed, skipped, totalRealizedPnl: state.totalRealizedPnl };
  }

  /** Get current hedge status for display. */
  getStatus(): { positions: HedgePosition[]; totalRealizedPnl: number; todayRealizedPnl: number } | null {
    if (!this.state) return null;
    return {
      positions: this.state.positions.filter(p => p.quantity > 0),
      totalRealizedPnl: this.state.totalRealizedPnl,
      todayRealizedPnl: this.state.todayRealizedPnl,
    };
  }
}
